import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

/**
 * Color-coded, shared church calendars.
 *
 * A `Calendar` is a color-coded category (Reunión, Camps, Vacaciones, ...);
 * a `CalendarEvent` belongs to exactly one and is either a single occurrence
 * or a simple indefinite weekly repeat — the same shape `Group.meetingDay`/
 * `meetingTime` already uses elsewhere in this schema set, not a full RRULE
 * engine. See `calendar.prisma` for the full rationale.
 */

export const RECURRENCE_FREQUENCIES = ['NONE', 'WEEKLY'] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

/** Where a rendered occurrence came from. Only `CALENDAR` is editable
 *  through this API — `EVENT`/`GROUP` occurrences are read live from their
 *  own services and link out to their own record. */
export const OCCURRENCE_SOURCES = ['CALENDAR', 'EVENT', 'GROUP'] as const;
export type OccurrenceSource = (typeof OCCURRENCE_SOURCES)[number];

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

// --- Calendar ---------------------------------------------------------------

export const createCalendarRequestSchema = z.object({
  name: z.string().trim().min(1).max(150),
  /** A fixed hex value from the picker's palette, not a token name. */
  color: z.string().regex(HEX_COLOR_PATTERN, 'Must be a hex color, e.g. #4F46E5'),
});
export type CreateCalendarRequest = z.infer<typeof createCalendarRequestSchema>;

/** Same shape as create — a calendar has only two editable fields, so a
 *  patch is just a full replace of both. */
export const updateCalendarRequestSchema = createCalendarRequestSchema;
export type UpdateCalendarRequest = z.infer<typeof updateCalendarRequestSchema>;

export const listCalendarsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListCalendarsQuery = z.infer<typeof listCalendarsQuerySchema>;

export interface CalendarResponse {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly archivedAt: string | null;
}

// --- CalendarEvent ------------------------------------------------------------

export const createCalendarEventRequestSchema = z
  .object({
    calendarId: z.uuid(),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullish(),
    location: z.string().trim().max(200).nullish(),
    isAllDay: z.boolean().default(false),
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    recurrenceFrequency: z.enum(RECURRENCE_FREQUENCIES).default('NONE'),
    /** Only meaningful when `recurrenceFrequency` is WEEKLY; ignored for NONE. */
    recurrenceEndDate: z.iso.date().nullish(),
  })
  .refine((value) => new Date(value.endAt).getTime() >= new Date(value.startAt).getTime(), {
    message: 'End must be at or after the start',
    path: ['endAt'],
  })
  .refine(
    (value) =>
      value.recurrenceFrequency !== 'WEEKLY' ||
      !value.recurrenceEndDate ||
      new Date(value.recurrenceEndDate).getTime() >= new Date(value.startAt).getTime(),
    {
      message: 'The recurrence end date must be on or after the start date',
      path: ['recurrenceEndDate'],
    },
  );
export type CreateCalendarEventRequest = z.infer<typeof createCalendarEventRequestSchema>;

/** Same shape as create — an event is replaced wholesale rather than
 *  patched field by field, matching how Campaigns and Events already do it. */
export const updateCalendarEventRequestSchema = createCalendarEventRequestSchema;
export type UpdateCalendarEventRequest = z.infer<typeof updateCalendarEventRequestSchema>;

export const listCalendarEventsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  calendarId: z.uuid().nullish(),
});
export type ListCalendarEventsQuery = z.infer<typeof listCalendarEventsQuerySchema>;

export interface CalendarEventResponse {
  readonly id: string;
  readonly calendarId: string;
  readonly title: string;
  readonly description: string | null;
  readonly location: string | null;
  readonly isAllDay: boolean;
  readonly startAt: string;
  readonly endAt: string;
  readonly recurrenceFrequency: RecurrenceFrequency;
  readonly recurrenceEndDate: string | null;
  readonly createdByUserId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Fixed pseudo-calendar identity for aggregated, non-editable sources — a
 *  published Event or a Group's weekly meeting time is never filed under a
 *  real `Calendar` row (see `calendar.prisma`'s header comment). The web
 *  client renders these as fixed, non-toggleable-off-by-default entries in
 *  the calendar visibility list, distinguished from a real `Calendar` id by
 *  never being a UUID. */
export const PSEUDO_CALENDARS = {
  EVENT: { id: 'pseudo-events', color: '#0EA5E9' },
  GROUP: { id: 'pseudo-groups', color: '#16A34A' },
} as const;

// --- Occurrences ------------------------------------------------------------

/** A bounded span — an unbounded range against an indefinite weekly
 *  recurrence would be unbounded expansion work. 92 days covers the widest
 *  span the month-grid view (a month plus lead-in/lead-out weeks) ever
 *  requests. */
export const MAX_OCCURRENCE_RANGE_DAYS = 92;

export const listOccurrencesQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .refine((value) => new Date(value.to).getTime() >= new Date(value.from).getTime(), {
    message: '`to` must be at or after `from`',
    path: ['to'],
  })
  .refine(
    (value) =>
      (new Date(value.to).getTime() - new Date(value.from).getTime()) / 86_400_000 <=
      MAX_OCCURRENCE_RANGE_DAYS,
    { message: `The range cannot exceed ${MAX_OCCURRENCE_RANGE_DAYS} days`, path: ['to'] },
  );
export type ListOccurrencesQuery = z.infer<typeof listOccurrencesQuerySchema>;

/** One rendered instance on the grid — a `CalendarEvent` shown once (NONE)
 *  or once per matching weekday in range (WEEKLY). Aggregated `EVENT`/
 *  `GROUP` sources join this same shape once cross-service aggregation
 *  lands; this service only ever emits `source: 'CALENDAR'` today. */
export interface OccurrenceResponse {
  readonly id: string;
  readonly source: OccurrenceSource;
  /** The underlying `CalendarEvent`/`Event`/`Group` id — only a `CALENDAR`
   *  source is actually editable through this API. */
  readonly sourceId: string;
  readonly calendarId: string;
  readonly calendarColor: string;
  readonly title: string;
  readonly location: string | null;
  readonly isAllDay: boolean;
  readonly startAt: string;
  readonly endAt: string;
}
